/**
 * Catalog facets — single source of truth for the /products filters and for
 * links into the machine create form.
 *
 * The GPU marketplace filters on:
 *   gpu     -> a GPU_PRODUCTS id (see lib/data/gpu-products.ts)
 *   region  -> an AirGPU region id (AIRGPU_REGIONS)
 *   priceMin / priceMax
 *
 * Read by GPUProductsPageClient. A value that is not one of these ids matches
 * nothing and the page falls through to the unfiltered grid — a link that looks
 * like it works and does not.
 *
 * The create form (/machines/create) pre-selects from:
 *   instanceType -> an AIRGPU_INSTANCE_TYPES key, e.g. "g5.2xlarge"
 *   region       -> an AirGPU region id
 * An unknown value is ignored and the form falls back to its defaults.
 *
 * The lists this replaces carried `useCase=gaming|rendering|video-editing|vr|
 * 3d-modeling|streaming` and `tier=…`. Neither `useCase` nor `tier` is a param
 * anything reads: `useCase` is a display-only string on the homepage cards. All
 * seven of those links were inert.
 *
 * Anything linking into /products must build its href from this module via
 * `productsHref()` rather than hand-writing a query string; anything linking
 * into the create form uses `machineCreateHref()`.
 */

import { GPU_PRODUCTS, getRegionName } from "@/lib/data/gpu-products";
import { AIRGPU_REGIONS, type AirGPURegion } from "@/lib/integrations/airgpu/types";

export interface FilterOption {
  /** Exact value written into the URL and matched against. */
  value: string;
  /** What the UI shows. */
  label: string;
}

/**
 * Rentable GPUs. Derived from GPU_PRODUCTS so the facet list cannot drift from
 * the catalog the page filters against — adding a GPU there adds it here.
 * Withdrawn products are left out: a link to a GPU nobody can rent is a dead
 * end, even though its card still shows on the unfiltered grid.
 */
export const GPU_TYPES: FilterOption[] = GPU_PRODUCTS.filter((gpu) => gpu.available).map((gpu) => ({
  value: gpu.id,
  label: gpu.name.replace(/^NVIDIA\s+/, ""),
}));

/**
 * Regions we offer, in the order of the region table. Derived from
 * AIRGPU_REGIONS, so it is exactly the set the create API accepts.
 * The label is the customer-facing name ("Europe (Frankfurt)") — never show
 * the raw value: it is an infrastructure code, not a place name.
 */
export const REGIONS: FilterOption[] = (Object.keys(AIRGPU_REGIONS) as AirGPURegion[]).map((id) => ({
  value: id,
  label: AIRGPU_REGIONS[id],
}));

export interface ProductsHrefFilters {
  /** A GPU_PRODUCTS id. */
  gpu?: string;
  /** An AirGPU region id, e.g. "us-east-1". */
  region?: string;
  priceMin?: number;
  priceMax?: number;
}

/** True when the value is a GPU the catalog actually offers. */
export const isValidGpu = (value: string | undefined): boolean =>
  !!value && GPU_TYPES.some((gpu) => gpu.value === value);

/** Display label for a GPU id; falls back to the id so an unknown one stays readable. */
export const gpuLabel = (value: string) =>
  GPU_TYPES.find((gpu) => gpu.value === value)?.label ?? value;

/**
 * Display label for a region id, including one a stored machine still uses
 * after the region was withdrawn (that falls back to the id).
 */
export const regionLabel = (value: string): string => getRegionName(value);

/**
 * Build a /products URL. Param names here must match what
 * GPUProductsPageClient reads from searchParams.
 */
export function productsHref(filters: ProductsHrefFilters = {}): string {
  const params = new URLSearchParams();
  if (filters.gpu) params.set("gpu", filters.gpu);
  if (filters.region) params.set("region", filters.region);
  if (filters.priceMin !== undefined && filters.priceMin > 0) {
    params.set("priceMin", filters.priceMin.toFixed(2));
  }
  if (filters.priceMax !== undefined) params.set("priceMax", filters.priceMax.toFixed(2));

  const query = params.toString();
  return query ? `/products?${query}` : "/products";
}

export interface MachineCreateHrefOptions {
  /** An AIRGPU_INSTANCE_TYPES key (GPUProduct.instanceType), e.g. "g5.2xlarge". */
  instanceType?: string;
  /** An AirGPU region id, e.g. "eu-central-1". */
  region?: string;
}

/**
 * Build a /machines/create URL that pre-selects a GPU and/or region. Param
 * names must match what CreateMachineForm reads from searchParams.
 */
export function machineCreateHref(options: MachineCreateHrefOptions = {}): string {
  const params = new URLSearchParams();
  if (options.instanceType) params.set("instanceType", options.instanceType);
  if (options.region) params.set("region", options.region);

  const query = params.toString();
  return query ? `/machines/create?${query}` : "/machines/create";
}
